<template>
  <div class="ai-composer">
    <!-- Top bar -->
    <div class="ai-topbar">
      <button class="btn btn-sm btn-light" @click="$router.push('/journeys')">
        <i class="bx bx-arrow-back"></i> Voltar
      </button>
      <div class="ai-title"><i class="bx bx-bot"></i> Criar Jornada com IA</div>
      <div style="width:70px"></div>
    </div>

    <div class="ai-body">
      <!-- Chat -->
      <div class="ai-chat">
        <div class="ai-messages" ref="messages">
          <div v-for="(m, i) in messages" :key="i" class="ai-msg" :class="'ai-msg-' + m.role">
            <div class="ai-bubble" v-html="format(m.text)"></div>
          </div>
          <div v-if="loading" class="ai-msg ai-msg-assistant">
            <div class="ai-bubble ai-typing"><span></span><span></span><span></span></div>
          </div>
        </div>

        <!-- Chips de opções (ask_user) -->
        <div v-if="askOptions.length" class="ai-chips">
          <button v-for="(opt, i) in askOptions" :key="i" class="ai-chip" @click="useOption(opt)">{{ opt }}</button>
        </div>

        <div class="ai-input">
          <textarea
            v-model="input"
            rows="1"
            placeholder="Descreva a jornada que você quer criar…"
            :disabled="loading || !sessionId"
            @keydown.enter.exact.prevent="send()"
          ></textarea>
          <button class="btn btn-primary" :disabled="loading || !sessionId || !input.trim()" @click="send()">
            <i class="bx bx-send"></i>
          </button>
        </div>
      </div>

      <!-- Preview -->
      <div class="ai-preview">
        <div class="ai-preview-head">
          <span><i class="bx bx-sitemap"></i> Prévia da jornada</span>
          <button v-if="journeyId" class="btn btn-sm btn-success" @click="openBuilder">
            <i class="bx bx-edit"></i> Abrir no builder
          </button>
        </div>

        <!-- Diagnósticos -->
        <div v-if="diagnostics && diagnostics.length" class="ai-diags">
          <div v-for="(d, i) in diagnostics" :key="i" class="ai-diag" :class="'ai-diag-' + d.severity">
            <strong>{{ diagLabel(d.severity) }}</strong> {{ d.message }}
            <div v-if="d.candidates && d.candidates.length" class="ai-cands">
              Candidatos: <span v-for="(c, j) in d.candidates" :key="j" class="ai-cand">{{ c.label }}</span>
            </div>
          </div>
        </div>

        <!-- Outline -->
        <div v-if="outlineLines.length" class="ai-outline">
          <div class="ai-outline-name">{{ outline.name }}</div>
          <div
            v-for="(l, i) in outlineLines"
            :key="i"
            class="ai-line"
            :class="{ 'ai-line-branch': l.branch }"
            :style="{ paddingLeft: (l.depth * 16 + 4) + 'px' }"
          >
            <span v-if="l.branch">├─ {{ l.text }}</span>
            <span v-else>• {{ l.text }}</span>
          </div>
        </div>

        <div v-else-if="!diagnostics || !diagnostics.length" class="ai-empty">
          <i class="bx bx-conversation"></i>
          <p>Converse com a IA à esquerda. Conforme o desenho fica pronto, a prévia aparece aqui.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import services from './services'
import { errorMessage } from '@/components/alertsMessages'

export default {
  name: 'journey-ai-composer',
  data () {
    return {
      sessionId: null,
      messages: [],
      input: '',
      loading: false,
      journeyId: null,
      outline: null,
      diagnostics: [],
      askOptions: []
    }
  },
  computed: {
    outlineLines () {
      const lines = []
      const walk = (steps, depth) => {
        (steps || []).forEach(s => {
          lines.push({ text: s.label, depth, branch: false })
          if (s.branches) {
            s.branches.forEach(b => {
              lines.push({ text: b.label, depth: depth + 1, branch: true })
              walk(b.steps, depth + 2)
            })
          }
        })
      }
      if (this.outline) walk(this.outline.steps, 0)
      return lines
    }
  },
  mounted () {
    this.start()
  },
  methods: {
    async start () {
      try {
        const res = await services.aiStart()
        this.sessionId = res.data.session_id
        this.messages.push({
          role: 'assistant',
          text: 'Olá! Sou o arquiteto de jornadas. Me conte o objetivo: qual evento, o que dispara a jornada e o que deve acontecer. Eu já monto um rascunho e a gente vai encaixando. 😊'
        })
      } catch (e) {
        errorMessage('Não foi possível iniciar o assistente.')
      }
    },
    async send (text) {
      const msg = (text !== undefined ? text : this.input).trim()
      if (!msg || this.loading || !this.sessionId) return
      this.messages.push({ role: 'user', text: msg })
      this.input = ''
      this.askOptions = []
      this.loading = true
      this.scrollDown()
      try {
        const res = await services.aiTurn(this.sessionId, msg)
        const d = res.data
        if (d.assistant) this.messages.push({ role: 'assistant', text: d.assistant })
        if (d.journey_id) this.journeyId = d.journey_id
        if (d.outline) this.outline = d.outline
        this.diagnostics = d.diagnostics || []
        if (d.ask_user && d.ask_user.options) this.askOptions = d.ask_user.options
      } catch (e) {
        const m = e.response && e.response.data ? e.response.data.message : e.message
        errorMessage('Alerta: ' + m)
      } finally {
        this.loading = false
        this.scrollDown()
      }
    },
    useOption (opt) {
      this.send(opt)
    },
    openBuilder () {
      if (this.journeyId) this.$router.push('/journeys/' + this.journeyId)
    },
    diagLabel (sev) {
      return { error: 'Erro:', warning: 'Atenção:', binding: 'Revisar:' }[sev] || ''
    },
    format (t) {
      return (t || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/\n/g, '<br>')
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    },
    scrollDown () {
      this.$nextTick(() => {
        const el = this.$refs.messages
        if (el) el.scrollTop = el.scrollHeight
      })
    }
  }
}
</script>

<style scoped>
.ai-composer { display: flex; flex-direction: column; height: 100vh; background: #f4f5f7; }
.ai-topbar { display: flex; align-items: center; justify-content: space-between; padding: 10px 16px; background: #fff; border-bottom: 1px solid #e5e7eb; }
.ai-title { font-weight: 600; font-size: 15px; }
.ai-body { flex: 1; display: flex; min-height: 0; }
.ai-chat { flex: 1.3; display: flex; flex-direction: column; border-right: 1px solid #e5e7eb; background: #fff; min-width: 0; }
.ai-messages { flex: 1; overflow-y: auto; padding: 18px; }
.ai-msg { display: flex; margin-bottom: 12px; }
.ai-msg-user { justify-content: flex-end; }
.ai-bubble { max-width: 78%; padding: 10px 14px; border-radius: 14px; font-size: 14px; line-height: 1.45; white-space: normal; }
.ai-msg-assistant .ai-bubble { background: #eef2ff; color: #1e293b; border-bottom-left-radius: 4px; }
.ai-msg-user .ai-bubble { background: #556ee6; color: #fff; border-bottom-right-radius: 4px; }
.ai-typing span { display: inline-block; width: 7px; height: 7px; margin: 0 2px; background: #94a3b8; border-radius: 50%; animation: blink 1.2s infinite both; }
.ai-typing span:nth-child(2) { animation-delay: .2s; }
.ai-typing span:nth-child(3) { animation-delay: .4s; }
@keyframes blink { 0%, 80%, 100% { opacity: .3; } 40% { opacity: 1; } }
.ai-chips { display: flex; flex-wrap: wrap; gap: 8px; padding: 0 18px 8px; }
.ai-chip { border: 1px solid #556ee6; color: #556ee6; background: #fff; border-radius: 16px; padding: 5px 12px; font-size: 13px; cursor: pointer; }
.ai-chip:hover { background: #556ee6; color: #fff; }
.ai-input { display: flex; gap: 8px; padding: 12px 16px; border-top: 1px solid #e5e7eb; }
.ai-input textarea { flex: 1; resize: none; border: 1px solid #ced4da; border-radius: 8px; padding: 9px 12px; font-size: 14px; max-height: 120px; }
.ai-preview { flex: 1; overflow-y: auto; padding: 16px; }
.ai-preview-head { display: flex; align-items: center; justify-content: space-between; font-weight: 600; margin-bottom: 12px; }
.ai-diags { margin-bottom: 14px; }
.ai-diag { font-size: 12.5px; padding: 7px 10px; border-radius: 6px; margin-bottom: 6px; }
.ai-diag-error { background: #fde8e8; color: #9b1c1c; }
.ai-diag-warning { background: #fdf6b2; color: #723b13; }
.ai-diag-binding { background: #e1effe; color: #1e429f; }
.ai-cands { margin-top: 4px; }
.ai-cand { display: inline-block; background: #fff; border: 1px solid #cdd7ea; border-radius: 10px; padding: 1px 7px; margin: 2px 3px 0 0; font-size: 11px; }
.ai-outline { background: #fff; border: 1px solid #e5e7eb; border-radius: 8px; padding: 12px; font-size: 13px; }
.ai-outline-name { font-weight: 600; margin-bottom: 8px; }
.ai-line { padding: 2px 4px; white-space: pre; font-family: inherit; }
.ai-line-branch { color: #556ee6; font-weight: 500; }
.ai-empty { text-align: center; color: #94a3b8; padding: 40px 20px; }
.ai-empty i { font-size: 40px; }
</style>
